import {
    ReactElement,
    useRef,
} from 'react';

import {
    Alert,
    Stack,
} from '@mantine/core';

import {usePhases} from '../hooks/usePhases.ts';

import {
    MantineDataTable,
    TableSearchForm,
    DataTableTokenPagination,
} from '@flexinet/ui-components';
import {usePhasesSelection} from '../hooks/usePhasesSelection.ts';
import {useVerifiedPermissions} from '@flexinet/auth-components';
import {usePhasesSearch} from "../hooks/usePhasesSearch.ts";
import {rbacActions} from "../../../config/rbac/actions.ts";
import {DataTableColumn} from "mantine-datatable";
import {useNavigate, useParams} from "react-router-dom";
import {Phase} from '../types.ts';

export const RecordsTable = ({
                                 extraTools,
                             }: {
    extraTools?: ReactElement[],
}) => {

    const {allowedActions} = useVerifiedPermissions()

    const navigate = useNavigate();

    // Scope list to the enclosing playbook when rendered inside /playbooks/:id.
    // This id reaches the queryKey through `filters` — see usePhasesQuery.
    const {id: playbookId} = useParams();
    const filters: Record<string, string> = playbookId ? {playbookId} : {};

    const {
        // The full loaded set BEFORE keyword filtering — the window the
        // footer's match count is measured against.
        items,
        visibleItems: records,
        columns,
        sortStatus,
        setSortStatus,
        loading,
        isError,
        isFetchingNextPage,
        nextToken,
        fetchNextPage,
    } = usePhases(filters);

    // Same module-scoped store the list hook filters on — this component owns
    // the input, usePhases owns the filtering.
    const {
        keyword,
        handleFilterByKeyword,
    } = usePhasesSearch();

    const {
        selection,
        handleRowSelection,
        handleClearSelection
    } = usePhasesSelection();


    let selectedRecords: Phase[] = [];
    if (allowedActions.includes(rbacActions.DeletePhase)) {
        selectedRecords = records.filter(record => selection[record.id] === true)
    }

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    // Two distinct empty states: nothing exists yet, versus nothing matched the
    // search. Collapsing them into one message tells an operator who mistyped a
    // keyword that this playbook has no phases at all.
    const noRecordsText = keyword
        ? `No phases match "${keyword}" in the records loaded so far.`
        : 'No phases yet.';

    return (
        <Stack>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            {extraTools}

            {isError && (
                <Alert color='red' title='Could not load phases'>
                    The workflows API did not return the list. Try Reload; if it persists the
                    API may be unavailable in this environment.
                </Alert>
            )}

            <MantineDataTable
                columns={columns as DataTableColumn[]}
                records={records}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={(items) => {
                    handleClearSelection();
                    items.forEach((item: any) => {
                        handleRowSelection(item.id, true)
                    })
                }}
                // Sorting is client-side over the pages loaded so far — the same
                // scope the keyword filter works on. Token pagination cannot
                // reorder rows the browser has not fetched.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                fetching={loading}
                noRecordsText={noRecordsText}
                onScrollToBottom={() => fetchNextPage()}
                scrollViewportRef={scrollViewportRef}
                // Same destination the Name column's NavLink points at, so the
                // whole row is a target rather than just the link text.
                onRowClick={({record}) => {
                    const phase = record as Phase;
                    navigate(`/playbooks/${playbookId ?? phase.playbookId}/phases/${encodeURIComponent(phase.id)}`);
                }}
            />

            <DataTableTokenPagination
                loadedCount={records.length}
                unfilteredCount={items.length}
                filterTerm={keyword}
                isFetchingMore={isFetchingNextPage}
                nextToken={nextToken}
                handleLoadMore={fetchNextPage}
                noun='phase'
            />
        </Stack>
    );
};
